import { WorkExperienceCompany, WorkExperienceRole, WorkExperienceBullet } from '@applicator/shared'
import React from 'react'


import { rolePositionEditorClass } from './editorTypography'
import {
  isBulletCheckboxChecked,
  isRoleLocationCheckboxChecked,
  isRolePositionCheckboxChecked,
} from '../../utils/workExperienceInclusion'
import WorkExperienceDragHandle from './WorkExperienceDragHandle'
import { WX_ADD_BULLET_BUTTON_CLASS, WX_BULLETS_BLOCK_CLASS } from './workExperienceLayout'
import { getSaveHint } from './utils'

const OPACITY_FULL = 'opacity-100'
const OPACITY_ZERO = 'opacity-0'

export interface WorkExperienceRoleItemProps {
  company: WorkExperienceCompany
  role: WorkExperienceRole
  /** `header` = position/dates only; `bullets` = bullet list (outside role drag card). */
  section?: 'header' | 'bullets'
  highlightedRoleId: string | null
  highlightedBulletId?: string | null
  editingRoleId: string | null
  editingRoleLocationId: string | null
  editingRolePosition: string
  editingRoleLocation: string
  editingBulletId: string | null
  editingBulletText: string
  hoveredRoleId: string | null
  hoveredBulletId: string | null
  draggedBulletId: string | null
  dragOverIndex: number | null
  bulletTextareaRef: React.RefObject<HTMLTextAreaElement>
  typeDropdownRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>
  onStartEditRole: (role: WorkExperienceRole) => void
  onSaveRole: (companyId: string, roleId: string) => void
  onStartEditRoleLocation: (role: WorkExperienceRole) => void
  onSaveRoleLocation: (companyId: string, roleId: string) => void
  onDeleteRole: (companyId: string, roleId: string) => void
  onTogglePosition: (companyId: string, roleId: string) => void
  onToggleLocation: (companyId: string, roleId: string) => void
  onAddBullet: (companyId: string, roleId: string) => void
  onToggleBullet: (companyId: string, roleId: string, bulletId: string) => void
  onDeleteBullet: (companyId: string, roleId: string, bulletId: string) => void
  onReorderBullets: (companyId: string, roleId: string, draggedBulletId: string, targetIndex: number) => void
  onStartEditBullet: (bullet: WorkExperienceBullet) => void
  onSaveBullet: (companyId: string, roleId: string, bulletId: string) => void
  onCancelEditBullet: () => void
  onOpenTypeDropdown: (companyId: string, roleId: string, event: React.MouseEvent) => void
  onStartEditRoleStartDate: (role: WorkExperienceRole, companyId: string, event: React.MouseEvent) => void
  onStartEditRoleEndDate: (role: WorkExperienceRole, companyId: string, event: React.MouseEvent) => void
  setHoveredRoleId: (id: string | null) => void
  setHoveredBulletId: (id: string | null) => void
  setHoveredCompanyId: (id: string | null) => void
  setDraggedBulletId: (id: string | null) => void
  setDragOverIndex: (index: number | null) => void
  setEditingRolePosition: (value: string) => void
  setEditingRoleLocation: (value: string) => void
  setEditingRoleId: (id: string | null) => void
  setEditingRoleLocationId: (id: string | null) => void
  setEditingBulletId: (id: string | null) => void
  setEditingBulletText: (value: string) => void
  adjustTextareaHeight: (textarea: HTMLTextAreaElement) => void
  handleBulletTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  renderCheckbox: (checked: boolean, onChange: () => void, size?: 'sm' | 'md') => React.ReactNode
}

export default function WorkExperienceRoleItem({
  company,
  role,
  section = 'header',
  highlightedRoleId: _highlightedRoleId,
  highlightedBulletId,
  editingRoleId,
  editingRoleLocationId,
  editingRolePosition,
  editingRoleLocation,
  editingBulletId,
  editingBulletText,
  hoveredRoleId,
  hoveredBulletId,
  draggedBulletId,
  dragOverIndex,
  bulletTextareaRef,
  typeDropdownRefs,
  dateFieldRefs,
  onStartEditRole,
  onSaveRole,
  onStartEditRoleLocation,
  onSaveRoleLocation,
  onDeleteRole,
  onTogglePosition,
  onToggleLocation,
  onAddBullet,
  onToggleBullet,
  onDeleteBullet,
  onReorderBullets,
  onStartEditBullet,
  onSaveBullet,
  onCancelEditBullet,
  onOpenTypeDropdown,
  onStartEditRoleStartDate,
  onStartEditRoleEndDate,
  setHoveredRoleId,
  setHoveredBulletId,
  setHoveredCompanyId,
  setDraggedBulletId,
  setDragOverIndex,
  setEditingRolePosition,
  setEditingRoleLocation,
  setEditingRoleId,
  setEditingRoleLocationId,
  _setEditingBulletId,
  _setEditingBulletText,
  adjustTextareaHeight,
  handleBulletTextChange,
  renderCheckbox
}: WorkExperienceRoleItemProps) {
  if (section === 'bullets') {
    return (
      <div className={WX_BULLETS_BLOCK_CLASS} data-role-id={role.id}>
              {/* Bullet Points */}
              {(role.bulletPoints || []).map((bullet, bulletIndex) => {
                const isDragging = draggedBulletId === bullet.id
                const isDragOver = dragOverIndex === bulletIndex && draggedBulletId !== bullet.id
                
                return (
                  <div
                    key={`${bullet.id}-${bulletIndex}`}
                    data-bullet-id={bullet.id}
                    data-wx-bullet-draggable="true"
                    draggable
                    onMouseEnter={() => {
                      setHoveredBulletId(bullet.id)
                      setHoveredRoleId(null)
                      setHoveredCompanyId(null)
                    }}
                    onMouseLeave={() => setHoveredBulletId(null)}
                    onDragStart={(e) => {
                      setDraggedBulletId(bullet.id)
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', '')
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      if (draggedBulletId !== bullet.id) {
                        setDragOverIndex(bulletIndex)
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverIndex === bulletIndex) {
                        setDragOverIndex(null)
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (draggedBulletId && draggedBulletId !== bullet.id) {
                        onReorderBullets(company.id, role.id, draggedBulletId, bulletIndex)
                      }
                      setDraggedBulletId(null)
                      setDragOverIndex(null)
                    }}
                    onDragEnd={() => {
                      setDraggedBulletId(null)
                      setDragOverIndex(null)
                    }}
                    className={`group/wx-bullet flex items-start gap-3 cursor-move ${
                      isDragging ? 'opacity-50' : ''
                    } ${isDragOver ? 'border-t-2 border-interactive pt-2 -mt-2' : ''} ${
                      highlightedBulletId === bullet.id
                        ? 'bg-brand-primary-subtle ring-2 ring-interactive-focus-ring ring-opacity-50 rounded'
                        : ''
                    }`}
                  >
                    <div 
                      className={`flex items-start gap-3 flex-1 rounded transition-colors ${
                        hoveredBulletId === bullet.id ? 'bg-surface-muted' : ''
                      }`}
                    >
                      <WorkExperienceDragHandle group="wx-bullet" title="Перетащите пункт для изменения порядка" />
                      <div
                        role="presentation"
                        className="pt-1"
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation()
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {renderCheckbox(
                          isBulletCheckboxChecked(company, role, bullet),
                          () => onToggleBullet(company.id, role.id, bullet.id),
                          'xs'
                        )}
                      </div>
                      <div className="flex-1 min-w-0 relative">
                        {editingBulletId === bullet.id ? (
                          <>
                            <textarea
                              ref={(el) => {
                                bulletTextareaRef.current = el
                                if (el) {
                                  setTimeout(() => adjustTextareaHeight(el), 0)
                                }
                              }}
                              value={editingBulletText}
                              onChange={handleBulletTextChange}
                              onBlur={() => onSaveBullet(company.id, role.id, bullet.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                  e.preventDefault()
                                  onSaveBullet(company.id, role.id, bullet.id)
                                } else if (e.key === 'Escape') {
                                  e.preventDefault()
                                  onCancelEditBullet()
                                }
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onFocus={(e) => adjustTextareaHeight(e.target)}
                              placeholder="Bullet point text"
                              rows={1}
                              className="w-full px-2 py-1 pb-5 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring resize-none overflow-hidden leading-relaxed"
                              style={{ minHeight: '2.5rem' }}
                              // eslint-disable-next-line jsx-a11y/no-autofocus
                              autoFocus
                            />
                            <div className="absolute bottom-1 right-2 text-xs text-text-muted pointer-events-none">
                              {getSaveHint()}
                            </div>
                          </>
                        ) : (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation()
                              onStartEditBullet(bullet)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                onStartEditBullet(bullet)
                              }
                            }}
                            className={`cursor-text rounded p-1 -m-1 text-sm leading-relaxed whitespace-pre-wrap ${
                              isBulletCheckboxChecked(company, role, bullet)
                                ? 'text-text-secondary'
                                : 'text-text-muted'
                            }`}
                          >
                            {bullet.text || 'Click to edit bullet point...'}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteBullet(company.id, role.id, bullet.id)
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                      }}
                      className={`p-1 text-text-muted hover:text-danger transition-opacity ${
                        hoveredBulletId === bullet.id ? OPACITY_FULL : OPACITY_ZERO
                      }`}
                      title="Delete bullet"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )
              })}
              <button
                onClick={() => onAddBullet(company.id, role.id)}
                className={WX_ADD_BULLET_BUTTON_CLASS}
              >
                + Add Bullet
              </button>
      </div>
    )
  }

  return (
<div 
              className="space-y-2"
              data-role-id={role.id}
            >
              {/* Position */}
              <div 
                className="flex items-center gap-3"
                onMouseEnter={() => {
                  setHoveredRoleId(role.id)
                  setHoveredCompanyId(null) // Clear company hover when hovering over role
                }}
                onMouseLeave={() => setHoveredRoleId(null)}
              >
                <div 
                  className={`flex items-center gap-3 flex-1 rounded transition-colors ${
                    hoveredRoleId === role.id ? 'bg-surface-muted' : ''
                  }`}
                >
                  {renderCheckbox(
                    isRolePositionCheckboxChecked(company, role),
                    () => onTogglePosition(company.id, role.id),
                    'xs'
                  )}
                  <div className="flex-1">
                    {editingRoleId === role.id ? (
                      <input
                        type="text"
                        value={editingRolePosition}
                        onChange={(e) => setEditingRolePosition(e.target.value)}
                        onBlur={() => onSaveRole(company.id, role.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            onSaveRole(company.id, role.id)
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setEditingRoleId(null)
                            setEditingRolePosition('')
                          }
                        }}
                        placeholder="Position"
                        className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => onStartEditRole(role)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onStartEditRole(role)
                          }
                        }}
                        className="cursor-text rounded p-1 -m-1"
                      >
                        <div className={`text-sm ${rolePositionEditorClass(company, role)}`}>
                          {role.position || 'Position'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Delete Role Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteRole(company.id, role.id)
                  }}
                  className={`p-1.5 text-text-muted hover:text-danger transition-opacity ${
                    hoveredRoleId === role.id ? 'opacity-100' : 'opacity-0'
                  }`}
                  title="Delete role"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Location */}
              <div className="flex items-center gap-3">
                {renderCheckbox(
                  isRoleLocationCheckboxChecked(company, role),
                  () => onToggleLocation(company.id, role.id),
                  'xs'
                )}
                <div className="flex-1">
                  <div className="text-xs text-text-secondary flex items-center gap-2 flex-wrap">
                    {editingRoleLocationId === role.id ? (
                      <input
                        type="text"
                        value={editingRoleLocation}
                        onChange={(e) => setEditingRoleLocation(e.target.value)}
                        onBlur={() => onSaveRoleLocation(company.id, role.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            onSaveRoleLocation(company.id, role.id)
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setEditingRoleLocationId(null)
                            setEditingRoleLocation('')
                          }
                        }}
                        placeholder="Location"
                        className="px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => onStartEditRoleLocation(role)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onStartEditRoleLocation(role)
                          }
                        }}
                        className="cursor-text hover:bg-surface-muted rounded px-1 -mx-1"
                      >
                        {role.location || <span className="text-text-muted italic">Location</span>}
                      </div>
                    )}

                    {/* Employment Type */}
                    <div
                      ref={(el) => {
                        const key = `${company.id}-${role.id}-type`
                        typeDropdownRefs.current[key] = el
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => onOpenTypeDropdown(company.id, role.id, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onOpenTypeDropdown(company.id, role.id, e as unknown as React.MouseEvent)
                        }
                      }}
                      className="cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1 text-xs text-text-secondary flex items-center gap-1"
                    >
                      <span>{role.employmentType || 'Type'}</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {/* Start Date */}
                    <div
                      ref={(el) => {
                        const key = `${company.id}-${role.id}-startDate`
                        dateFieldRefs.current[key] = el
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => onStartEditRoleStartDate(role, company.id, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onStartEditRoleStartDate(role, company.id, e as unknown as React.MouseEvent)
                        }
                      }}
                      className="cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1"
                    >
                      {role.startDate || <span className="text-text-muted italic">Start date</span>}
                    </div>

                    {/* Separator */}
                    {(role.startDate || role.endDate) && <span>-</span>}

                    {/* End Date */}
                    <div
                      ref={(el) => {
                        const key = `${company.id}-${role.id}-endDate`
                        dateFieldRefs.current[key] = el
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => onStartEditRoleEndDate(role, company.id, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onStartEditRoleEndDate(role, company.id, e as unknown as React.MouseEvent)
                        }
                      }}
                      className="cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1"
                    >
                      {role.endDate || <span className="text-text-muted italic">End date</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
  )
}
